import { TikTokPage } from '@remotion/captions';
import React, { useRef, useEffect, useState } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import {
  FontStyle,
  WORD_BREAK_STYLE,
  TextAlign,
  TextDirection,
  TextShadow,
  TextItemBackground,
  defaultTextItemBackground,
} from '../text/text-item-type';
import { turnFontStyleIntoCss } from '../../utils/text/turn-font-style-into-css';
import { fitTokensOnNLines } from './fit-tokens-on-n-lines';
import { resolveFontFamilyCssStack } from '../../utils/text/font-family-css-stack';
import { getActiveWordTimingIndex } from '@/shared/lib/captions/active-word-timing';
import { offsetCaptionWordsByDisplayLead } from '@/shared/lib/captions/caption-presentation-timing';
import {
  isCaptionKaraokeHighlightEffect,
  type CaptionHighlightEffect,
} from '@/shared/lib/captions/caption-highlight-effect';
import { getCaptionTokenDisplayText } from '@/shared/lib/captions/caption-token-display-text';
import { getKaraokeHighlightWords } from '@/shared/lib/captions/karaoke-highlight-progress';
import { getKaraokeTextFillStyle } from '@/shared/lib/captions/karaoke-text-fill-style';
import { usePresentedCaptionTimeMs } from './use-presented-caption-time';
import {sourceTokenRows,type WakaSourceTypography} from '@waka/compat/source-caption-presentation';

const CAPTION_WIDTH_SAFETY_PX = 2;

interface CaptionRenderToken {
  key: string;
  text: string;
  active: boolean;
  fillRatio: number;
}

export const CaptionPage: React.FC<{
  sourceTypography?:WakaSourceTypography;
  page: TikTokPage;
  captionWidth: number;
  captionHeight: number;
  fontFamily: string;
  fontStyle: FontStyle;
  lineHeight: number;
  letterSpacing: number;
  color: string;
  highlightColor?: string | null;
  highlightMode?: 'cue' | 'word';
  highlightEffect?: CaptionHighlightEffect;
  direction: TextDirection;
  align: TextAlign;
  fontSize: number;
  maxLines: number;
  shadow: TextShadow;
  shadowVisible?: boolean;
  strokeWidth: number;
  strokeColor: string;
  background?: TextItemBackground | null;
  backgroundOpacity?: number;
  presentationLeadMs?: number;
  sequenceStartMs?: number;
  usePresentedMediaClock?: boolean;
  preserveWordEndTiming?: boolean;
}> = ({
  sourceTypography,
  page,
  captionWidth,
  captionHeight,
  fontFamily,
  fontStyle,
  lineHeight,
  letterSpacing,
  color,
  highlightColor,
  highlightMode = 'word',
  highlightEffect = 'instant',
  direction,
  align,
  fontSize: desiredFontSize,
  maxLines,
  shadow,
  shadowVisible = true,
  strokeWidth,
  strokeColor,
  background,
  backgroundOpacity = 1,
  presentationLeadMs = 0,
  sequenceStartMs = 0,
  usePresentedMediaClock = false,
  preserveWordEndTiming = false,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const timeInMs = (frame / fps) * 1000;
  const presentationTimeInMs = usePresentedCaptionTimeMs({
    enabled: usePresentedMediaClock,
    sequenceTimeMs: timeInMs,
    sequenceStartMs,
  });

  const safeMaxWidth = Math.max(0, captionWidth - CAPTION_WIDTH_SAFETY_PX);
  const cssFontFamily = resolveFontFamilyCssStack(fontFamily);

  // 토큰 span 구조로 폰트 크기를 계산하여 실제 렌더링과 측정의 일치를 보장한다.
  // fitTextOnNLines(단일 텍스트)는 토큰 span 렌더링과 줄바꿈 동작이 달라 3줄 넘침이 발생할 수 있다.
  const fittedText = sourceTypography?.preserveFontSize ? {fontSize:desiredFontSize} : fitTokensOnNLines({
    tokens: page.tokens,
    maxBoxWidth: safeMaxWidth,
    maxLines: maxLines,
    maxFontSize: desiredFontSize,
    fontFamily: cssFontFamily,
    fontStyle,
    letterSpacing,
    lineHeight,
  });

  const fontSize = Math.min(desiredFontSize, fittedText.fontSize);

  const style: React.CSSProperties = React.useMemo(
    () => ({
      fontSize: fontSize,
      color: 'white',
      fontFamily: cssFontFamily,
      height: '100%',
      width: '100%',
      ...turnFontStyleIntoCss(fontStyle),
      lineHeight: String(lineHeight),
      letterSpacing: `${letterSpacing}px`,
      textShadow: sourceTypography?.textShadow,
      textAlign: align,
      ...WORD_BREAK_STYLE,
      textWrap: sourceTypography ? 'wrap' : 'balance',
      display: 'flex',
      alignItems: sourceTypography && !sourceTypography.bar && sourceTypography.verticalAlign === 'top' ? 'flex-start' : 'center',
      justifyContent: 'center',
      position: 'relative',
    }),
    [fontSize, cssFontFamily, fontStyle, lineHeight, letterSpacing, align,sourceTypography],
  );

  const presentationWords = React.useMemo(
    () =>
      offsetCaptionWordsByDisplayLead(
        page.tokens.map((token, index) => ({
          text: getCaptionTokenDisplayText(page.tokens, index),
          startMs: token.fromMs - page.startMs,
          endMs: token.toMs - page.startMs,
        })),
        presentationLeadMs,
      ),
    [page.startMs, page.tokens, presentationLeadMs],
  );

  const activeTokenIndex = React.useMemo(
    () => getActiveWordTimingIndex(presentationWords, presentationTimeInMs),
    [presentationWords, presentationTimeInMs],
  );

  const karaokeWords = React.useMemo(
    () =>
      isCaptionKaraokeHighlightEffect(highlightEffect) &&
      highlightMode !== 'cue'
        ? getKaraokeHighlightWords(
            presentationWords,
            presentationTimeInMs,
            highlightEffect,
            { preserveWordEndTiming },
          )
        : null,
    [
      highlightEffect,
      highlightMode,
      preserveWordEndTiming,
      presentationTimeInMs,
      presentationWords,
    ],
  );

  const tokens = React.useMemo(
    () =>
      page.tokens.map((t, index) => {
        const active =
          highlightMode === 'cue' ? true : activeTokenIndex === index;
        const fillRatio =
          karaokeWords !== null
            ? (karaokeWords[index]?.fillRatio ?? 0)
            : active
              ? 1
              : 0;

        return {
          key: `${t.fromMs}-${t.text}`,
          text: presentationWords[index]?.text ?? '',
          active,
          fillRatio,
        };
      }),
    [
      activeTokenIndex,
      highlightMode,
      karaokeWords,
      page.tokens,
      presentationWords,
    ],
  );

  const sourceRows=React.useMemo(()=>sourceTypography?sourceTokenRows(tokens,captionWidth,desiredFontSize,sourceTypography):null,
    [tokens,captionWidth,desiredFontSize,sourceTypography]);
  const renderRows=(renderToken:(token:CaptionRenderToken)=>React.ReactNode)=>sourceRows
    ? sourceRows.map((row,index)=><span key={index} data-waka-caption-row style={{display:'block',width:'100%',textAlign:align,whiteSpace:'pre-line',fontFamily:'inherit'}}>{row.map(renderToken)}</span>)
    : tokens.map(renderToken);

  // 배경 설정
  const bgSettings = React.useMemo(() => {
    return {
      ...defaultTextItemBackground,
      ...(background ?? {}),
    };
  }, [background]);

  // text-only 모드일 때 wrapper에 직접 배경 스타일 적용
  const shouldShowTextBackground =
    bgSettings.visible && bgSettings.mode === 'text-only';
  const textStroke = strokeWidth ? `${strokeWidth}px ${strokeColor}` : '0';
  const usesKaraokeHighlight = isCaptionKaraokeHighlightEffect(highlightEffect);
  const shouldRenderKaraokeStrokeLayer =
    usesKaraokeHighlight && strokeWidth > 0;

  const wrapperStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      position: 'relative',
      display: 'inline-block',
      whiteSpace: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
      textAlign: 'inherit',
      fontFamily: 'inherit',
      ...(sourceTypography?.bar?{width:'max-content',maxWidth:'100%',boxSizing:'border-box',background:sourceTypography.bar.color,borderRadius:sourceTypography.bar.radius,padding:`${sourceTypography.bar.paddingY}px ${sourceTypography.bar.paddingX}px`,boxShadow:sourceTypography.bar.shadow,...(sourceTypography.bar.borderColor?{border:`${sourceTypography.bar.borderWidth}px solid ${sourceTypography.bar.borderColor}`}:{})}:sourceTypography?{width:'100%'}:{}),
    };
  }, [sourceTypography]);

  // 텍스트 줄 측정을 위한 ref와 state
  const textSpanRef = useRef<HTMLSpanElement>(null);
  const [lineMeasurements, setLineMeasurements] = useState<
    Array<{ width: number; height: number; top: number; left: number }>
  >([]);

  // 캡션 영역 div에 대한 ref (스케일 계산용)
  const captionAreaRef = useRef<HTMLDivElement>(null);

  // 텍스트 렌더링 후 줄별 위치 측정
  useEffect(() => {
    const clearLineMeasurements = () => {
      setLineMeasurements((current) => (current.length === 0 ? current : []));
    };

    const updateLineMeasurements = (
      lines: Array<{
        width: number;
        height: number;
        top: number;
        left: number;
      }>,
    ) => {
      setLineMeasurements((current) => {
        if (
          current.length === lines.length &&
          current.every((line, index) => {
            const nextLine = lines[index];
            if (!nextLine) return false;
            return (
              line.width === nextLine.width &&
              line.height === nextLine.height &&
              line.top === nextLine.top &&
              line.left === nextLine.left
            );
          })
        ) {
          return current;
        }
        return lines;
      });
    };

    if (!shouldShowTextBackground || !textSpanRef.current) {
      clearLineMeasurements();
      return;
    }

    const container = textSpanRef.current;
    const containerRect = container.getBoundingClientRect();

    // 스케일 계산: Remotion 프리뷰에서 CSS transform으로 스케일링되므로
    // 스크린 픽셀 -> 논리적 좌표로 변환 필요
    // 캡션 영역 div의 실제 렌더링 크기 vs 논리적 크기로 스케일 계산
    const captionAreaDiv = captionAreaRef.current;
    let scaleX = 1;
    let scaleY = 1;

    if (captionAreaDiv && captionWidth > 0 && captionHeight > 0) {
      const captionAreaRect = captionAreaDiv.getBoundingClientRect();
      scaleX = captionAreaRect.width / captionWidth;
      scaleY = captionAreaRect.height / captionHeight;
    }

    // 컨테이너 내의 모든 텍스트 노드 찾기
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );

    const textNodes: Text[] = [];
    let node = walker.nextNode();
    while (node) {
      const textNode = node as Text;
      if (
        !textNode.parentElement?.closest(
          '[data-caption-measure-exclude="true"]',
        )
      ) {
        textNodes.push(textNode);
      }
      node = walker.nextNode();
    }

    if (textNodes.length === 0) {
      clearLineMeasurements();
      return;
    }

    const range = document.createRange();
    const lines: Array<{
      width: number;
      height: number;
      top: number;
      left: number;
    }> = [];

    let currentLineTop: number | null = null;
    let currentLineLeft = Infinity;
    let currentLineRight = 0;
    let currentLineHeight = 0;

    // 모든 텍스트 노드의 각 문자를 순회하며 줄 경계 감지
    for (const textNode of textNodes) {
      const nodeText = textNode.textContent || '';

      for (let i = 0; i < nodeText.length; i++) {
        if (nodeText[i] === '\n') {
          if (currentLineTop !== null && currentLineRight > currentLineLeft) {
            // 스케일 보정 적용: 스크린 픽셀 -> 논리적 좌표
            lines.push({
              width: (currentLineRight - currentLineLeft) / scaleX,
              height: currentLineHeight / scaleY,
              top: currentLineTop / scaleY,
              left: currentLineLeft / scaleX,
            });
          }
          currentLineTop = null;
          currentLineLeft = Infinity;
          currentLineRight = 0;
          currentLineHeight = 0;
          continue;
        }

        try {
          range.setStart(textNode, i);
          range.setEnd(textNode, i + 1);
          const rects = range.getClientRects();

          const rect = rects[0];
          if (rect && rect.width > 0) {
            const relativeTop = rect.top - containerRect.top;
            const relativeLeft = rect.left - containerRect.left;
            const relativeRight = rect.right - containerRect.left;

            if (currentLineTop === null) {
              currentLineTop = relativeTop;
              currentLineHeight = rect.height;
            } else if (Math.abs(relativeTop - currentLineTop) > 2) {
              // 이전 줄 저장 (스케일 보정 적용)
              if (currentLineRight > currentLineLeft) {
                lines.push({
                  width: (currentLineRight - currentLineLeft) / scaleX,
                  height: currentLineHeight / scaleY,
                  top: currentLineTop / scaleY,
                  left: currentLineLeft / scaleX,
                });
              }
              currentLineTop = relativeTop;
              currentLineLeft = Infinity;
              currentLineRight = 0;
              currentLineHeight = rect.height;
            }

            currentLineLeft = Math.min(currentLineLeft, relativeLeft);
            currentLineRight = Math.max(currentLineRight, relativeRight);
            currentLineHeight = Math.max(currentLineHeight, rect.height);
          }
        } catch {
          // Range 설정 실패 시 무시
        }
      }
    }

    // 마지막 줄 저장 (스케일 보정 적용)
    if (currentLineTop !== null && currentLineRight > currentLineLeft) {
      lines.push({
        width: (currentLineRight - currentLineLeft) / scaleX,
        height: currentLineHeight / scaleY,
        top: currentLineTop / scaleY,
        left: currentLineLeft / scaleX,
      });
    }

    updateLineMeasurements(lines);
  }, [
    shouldShowTextBackground,
    fontSize,
    page.text,
    cssFontFamily,
    fontStyle,
    lineHeight,
    letterSpacing,
    captionWidth,
    captionHeight,
  ]);

  return (
    <AbsoluteFill>
      <div
        ref={captionAreaRef}
        dir={direction}
        style={style}
      >
        <span style={wrapperStyle}>
          {/*
           * 렌더링 순서 (뒤에서 앞으로): 배경 -> shadow -> 텍스트
           * 배경을 wrapper 레벨에 두어 shadow보다 뒤에 위치하도록 함
           */}
          {/* 1. 배경 (가장 뒤, zIndex: -2) */}
          {shouldShowTextBackground &&
            lineMeasurements.map((line, index) => (
              <span
                key={`bg-line-${index}`}
                aria-hidden
                style={{
                  position: 'absolute',
                  left: line.left - bgSettings.horizontalPadding,
                  // 폰트별 시각적 수직 중앙 보정 (사용자 설정 가능)
                  top:
                    line.top -
                    bgSettings.verticalPadding -
                    line.height * (bgSettings.verticalOffset / 100),
                  width: line.width + bgSettings.horizontalPadding * 2,
                  height: line.height + bgSettings.verticalPadding * 2,
                  backgroundColor: bgSettings.color,
                  opacity: backgroundOpacity * bgSettings.opacity,
                  borderRadius: bgSettings.borderRadius,
                  pointerEvents: 'none',
                  zIndex: -2,
                }}
              />
            ))}
          {/* 2. Shadow (중간, zIndex: -1) */}
          {shadow?.visible && shadowVisible && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: shadow.offsetX,
                top: shadow.offsetY,
                color: shadow.color,
                opacity: shadow.opacity,
                filter: shadow.blur > 0 ? `blur(${shadow.blur}px)` : undefined,
                pointerEvents: 'none',
                userSelect: 'none',
                display: 'block',
                whiteSpace: 'inherit',
                lineHeight: 'inherit',
                letterSpacing: 'inherit',
                width: '100%',
                zIndex: -1,
                fontFamily: 'inherit',
                ...turnFontStyleIntoCss(fontStyle),
              }}
            >
              {renderRows((token) => (
                <span
                  key={`shadow-${token.key}`}
                  style={{
                    display: 'inline',
                    whiteSpace: 'pre-wrap',
                    color: shadow.color,
                    fontFamily: 'inherit',
                  }}
                >
                  {token.text}
                </span>
              ))}
            </span>
          )}
          {/* 3. 텍스트 (가장 앞, zIndex: 0) */}
          <span
            ref={textSpanRef}
            style={{
              position: 'relative',
              display: 'block',
              whiteSpace: 'inherit',
              zIndex: 0,
              fontFamily: 'inherit',
              ...turnFontStyleIntoCss(fontStyle),
              WebkitTextStroke: usesKaraokeHighlight ? '0' : textStroke,
              paintOrder: 'stroke',
            }}
          >
            {usesKaraokeHighlight ? (
              <>
                {shouldRenderKaraokeStrokeLayer && (
                  <span
                    aria-hidden
                    data-caption-measure-exclude="true"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      display: 'block',
                      whiteSpace: 'inherit',
                      lineHeight: 'inherit',
                      letterSpacing: 'inherit',
                      width: '100%',
                      zIndex: 0,
                      fontFamily: 'inherit',
                      ...turnFontStyleIntoCss(fontStyle),
                      color: 'transparent',
                      WebkitTextFillColor: 'transparent',
                      WebkitTextStroke: textStroke,
                      paintOrder: 'stroke',
                    }}
                  >
                    {renderRows((token) => (
                      <span
                        key={`stroke-${token.key}`}
                        style={{
                          display: 'inline-block',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                          color: 'transparent',
                          WebkitTextFillColor: 'transparent',
                          WebkitTextStroke: 'inherit',
                        }}
                      >
                        {token.text}
                      </span>
                    ))}
                  </span>
                )}
                <span
                  style={{
                    position: 'relative',
                    display: 'block',
                    whiteSpace: 'inherit',
                    zIndex: 1,
                    fontFamily: 'inherit',
                    WebkitTextStroke: '0',
                  }}
                >
                  {renderRows((token) =>
                    renderKaraokeToken({
                      token,
                      color,
                      highlightColor,
                    }),
                  )}
                </span>
              </>
            ) : (
              renderRows((token) => (
                <span
                  key={token.key}
                  style={{
                    display: 'inline',
                    whiteSpace: 'pre-wrap',
                    color: token.active ? (highlightColor ?? color) : color,
                    fontFamily: 'inherit',
                  }}
                >
                  {token.text}
                </span>
              ))
            )}
          </span>
        </span>
      </div>
    </AbsoluteFill>
  );
};

function renderKaraokeToken({
  token,
  color,
  highlightColor,
}: {
  token: CaptionRenderToken;
  color: string;
  highlightColor?: string | null;
}) {
  return (
    <span
      key={token.key}
      style={{
        display: 'inline-block',
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        ...getKaraokeTextFillStyle({
          fillRatio: token.fillRatio,
          fontColor: color,
          highlightColor,
        }),
      }}
    >
      {token.text}
    </span>
  );
}
